import { Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import { BetBuilderFixture, BetBuilderOptionGroupInfo } from '@cds/betting-offer/domain-specific/bet-builder';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { filterSportsEmitLast } from '@frontend/sports/host-app/sports-product/feature/utils';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash-es';
import { BetslipType } from 'packages/sports/common/betslip/core/betslip-type';
import { ExternalBetslipActions } from 'packages/sports/common/betslip/core/external-betslip-actions';
import { V1PickId, V2OptionMarketPickId } from 'packages/sports/common/betslip/core/picks/pick-id';
import { IPickTracking } from 'packages/sports/common/betslip/core/picks/pick-models';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';
import BetslipSingleBetActions from 'packages/sports/common/betslip/modules/single-bet/actions';
import { Observable, combineLatest } from 'rxjs';

import { BetBuilderMarket } from '../../betbuilder/model/bet-builder.model';
import { BetBuilderPickIdType, IBetBuilderBetSlipService } from './betslip-betbuilder-services/bet-builder-betslip-service.model';
import { BetBuilderBetslipService } from './betslip-betbuilder-services/bet-builder-betslip.service';
import { BetBuilderGroupBetslipService } from './betslip-betbuilder-services/bet-builder-group-betslip.service';
import { Selection } from './model';

@Injectable()
export class BetslipConnectorService {
    private currentPickCount = 0;

    constructor(
        private betslipIntegrationService: BetslipIntegrationService,
        private store: Store,
        private betslipConfig: BetslipConfig,
        private betBuilderBetslipService: BetBuilderBetslipService,
        private betBuilderGroupBetslipService: BetBuilderGroupBetslipService,
    ) {
        betslipIntegrationService
            .betslipPicksCount$()
            .pipe(filterSportsEmitLast())
            .subscribe((count) => (this.currentPickCount = count));
    }

    addToBetslip(selections: Selection[], stake: number, pickSource: IPickTracking): void {
        const payload = {
            picks: selections.map((s) => ({
                pickId: this.createPickId(s),
                tracking: pickSource,
            })),
            stake,
            // TODO: port this change from master
            // sourceOfPick: SourceOfPick.MultiGenerator,
        };
        this.betslipIntegrationService.addMultiplePicks(payload);
        payload.picks.forEach((pick) => {
            this.store.dispatch(BetslipSingleBetActions.setSingleBetPickStake({ pickId: pick.pickId.toString(), stake }));
        });
        this.store.dispatch(ExternalBetslipActions.requestBetslipType({ betslipType: BetslipType.Combo }));
    }

    removeFromBetslip(selections: Selection[]): void {
        const payload = {
            pickIds: selections.map((s) => this.createPickId(s)),
        };
        this.betslipIntegrationService.removeMultiplePicks(payload);
    }

    canBeAdded(count: number): boolean {
        return count + this.currentPickCount <= this.betslipConfig.maxBetslipBets;
    }

    addBetBuilderPickToBetslip(
        event: EventModel,
        betBuilderMarket: BetBuilderMarket | undefined,
        tracking: IPickTracking,
        betBuilderPickIds: BetBuilderPickIdType[],
        preCreatedOffer: BetBuilderFixture,
    ): void {
        const betBuilderService = this.getApplicableBetBuilder(preCreatedOffer);
        if (betBuilderService) {
            betBuilderService.addToBetslip({ event, betBuilderMarket, tracking, betBuilderPickIds });
        }
    }

    removeBetBuilderPick(preCreatedOffer: BetBuilderFixture, pickIds: BetBuilderPickIdType[], eventId: string, longId?: string): void {
        const betBuilderService = this.getApplicableBetBuilder(preCreatedOffer);
        if (betBuilderService) {
            betBuilderService.removeFromBetslip({ pickIds, eventId, longId });
        }
    }

    getPicksSelected(selections: Selection[], betBuilderPickIds: BetBuilderPickIdType[]): Observable<boolean[]> {
        const picks = [...this.getPickIds(selections), ...betBuilderPickIds];

        return combineLatest(picks.map((pickId) => this.betslipIntegrationService.pickSelected$(pickId)));
    }

    private getPickIds(selections: Selection[]): (V1PickId | V2OptionMarketPickId)[] {
        return selections.map((s) => this.createPickId(s));
    }

    private createPickId(selection: Selection): V1PickId | V2OptionMarketPickId {
        if (selection.event.offerSource === OfferSource.V1 && selection.optionGroup.source !== OfferSource.V2) {
            return new V1PickId(selection.option.originalEventId || selection.event.id, selection.option.optionGroupId, selection.option.id);
        } else {
            return new V2OptionMarketPickId(
                selection.event.hybridFixtureData != null
                    ? selection.event.hybridFixtureData?.fixtureId
                    : selection.option.originalEventId || selection.event.id,
                selection.option.optionGroupId,
                selection.option.id,
                selection.option.priceId!,
            );
        }
    }

    getBetBuilderPicks(
        preCreatedOffer: BetBuilderFixture,
        betBuilderMarket?: BetBuilderMarket,
        eventId?: string,
        betBuilderFixtureId?: string,
        precreatedGroup?: BetBuilderOptionGroupInfo,
        eventOptionGroups?: EventOptionGroup[],
    ): BetBuilderPickIdType[] {
        const betBuilderService = this.getApplicableBetBuilder(preCreatedOffer);
        return betBuilderService.createPicks({
            betBuilderMarket,
            eventId,
            betBuilderFixtureId,
            precreatedGroup,
            eventOptionGroups,
        });
    }

    private getApplicableBetBuilder(preCreatedOffer: BetBuilderFixture): IBetBuilderBetSlipService {
        // Take this decision based on provider when all labels are supported precreated SGP for InternalBYB
        return isEmpty(preCreatedOffer.precreatedGroupedInfo) ? this.betBuilderBetslipService : this.betBuilderGroupBetslipService;
    }
}
